import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ExternalLink } from "lucide-react";
import cvUrl from "@/assets/CV-Mathys-Gautier.pdf?url";
import { sanitizeRichText } from "@/lib/sanitize";
import { parseFactValues } from "@/lib/fact-values";

export const Route = createFileRoute("/parcours")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
      { title: "Parcours et CV — Mathys Gautier" },
      {
        name: "description",
        content:
          "Rédactions, formations et engagements bénévoles : découvrez les jalons du parcours de Mathys Gautier dans le journalisme et ailleurs, ainsi que son CV.",
      },
      { name: "robots", content: "noimageindex" },
      { property: "og:title", content: "Parcours et CV — Mathys Gautier" },

      {
        property: "og:description",
        content:
          "Rédactions, formations et engagements bénévoles : découvrez les jalons du parcours de Mathys Gautier dans le journalisme et ailleurs, ainsi que son CV.",
      },
      { property: "og:url", content: "https://mathysgautier.com/parcours" },
    ],
    links: [{ rel: "canonical", href: "https://mathysgautier.com/parcours" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          name: "Parcours et CV — Mathys Gautier",
          description: "Rédactions, formations et engagements bénévoles : découvrez les jalons du parcours de Mathys Gautier dans le journalisme et ailleurs, ainsi que son CV.",
          url: "https://mathysgautier.com/parcours",
          isPartOf: {
            "@type": "WebSite",
            name: "Mathys Gautier",
            url: "https://mathysgautier.com",
          },
          about: {
            "@type": "Person",
            name: "Mathys Gautier",
            jobTitle: "Journaliste",
            url: "https://mathysgautier.com",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Accueil",
              item: "https://mathysgautier.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Parcours",
              item: "https://mathysgautier.com/parcours",
            },
          ],
        }),
      },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(parcoursQueryOptions),
  errorComponent: ({ error }) => (
    <div role="alert" className="mx-auto max-w-5xl px-6 py-24 text-sm">
      {error instanceof Error ? error.message : "Une erreur est survenue."}
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-5xl px-6 py-24 text-sm">Contenu introuvable.</div>
  ),
  component: Parcours,
});

interface Entry {
  period: string;
  duration?: string;
  role: string;
  place: string;
  type?: string;
  city?: string;
  logo?: { url: string; alt: string };
  bullets: string[];
  href?: string;
}

type EntryRow = {
  id: string;
  category: string;
  period: string;
  duration: string | null;
  role: string;
  place: string;
  type: string | null;
  city: string | null;
  logo_url: string | null;
  logo_alt: string | null;
  bullets: unknown;
  href: string | null;
  display_order: number;
};

type FactRow = {
  id: string;
  category: string;
  label: string;
  value: string;
  display_order: number;
};

function toEntry(r: EntryRow): Entry {
  return {
    period: r.period,
    duration: r.duration ?? undefined,
    role: r.role,
    place: r.place,
    type: r.type ?? undefined,
    city: r.city ?? undefined,
    logo: r.logo_url && r.logo_alt ? { url: r.logo_url, alt: r.logo_alt } : undefined,
    bullets: Array.isArray(r.bullets)
      ? (r.bullets as unknown[]).filter((b): b is string => typeof b === "string")
      : [],
    href: r.href ?? undefined,
  };
}

type CategoryRow = {
  id: string;
  slug: string;
  label: string;
  display_order: number;
};

const parcoursQueryOptions = queryOptions({
  queryKey: ["parcours"],
  queryFn: async () => {
    const [entriesRes, factsRes, categoriesRes] = await Promise.all([
      supabase
        .from("parcours_entries")
        .select(
          "id,category,period,duration,role,place,type,city,logo_url,logo_alt,bullets,href,display_order",
        )
        .eq("is_published", true)
        .order("display_order", { ascending: true }),
      supabase
        .from("parcours_facts")
        .select("id,category,label,value,display_order")
        .eq("is_published", true)
        .order("display_order", { ascending: true }),
      supabase
        .from("parcours_categories")
        .select("id,slug,label,display_order")
        .order("display_order", { ascending: true }),
    ]);
    if (entriesRes.error) throw entriesRes.error;
    if (factsRes.error) throw factsRes.error;
    if (categoriesRes.error) throw categoriesRes.error;
    return {
      entries: (entriesRes.data ?? []) as EntryRow[],
      facts: (factsRes.data ?? []) as FactRow[],
      categories: (categoriesRes.data ?? []) as CategoryRow[],
    };
  },
});

const FACT_SLUGS = new Set(["competences", "langues"]);

function FactBlock({
  id,
  title,
  facts,
}: {
  id: string;
  title: string;
  facts: { k: string; v: string }[];
}) {
  return (
    <div
      id={id}
      className="grid scroll-mt-24 gap-8 border-t border-border py-12 lg:grid-cols-[240px_1fr]"
    >
      <h2 className="font-serif text-3xl leading-tight">{title}</h2>
      <dl className="grid gap-8 sm:grid-cols-2">
        {facts.map((c) => {
          const items = parseFactValues(c.v);
          return (
            <div key={c.k}>
              <dt className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{c.k}</dt>
              <dd className="mt-2 text-sm leading-relaxed text-foreground/85">
                <ul className="space-y-1">
                  {items.map((item, i) => (
                    <li key={i}>
                      <span
                        className="[&_a]:border-b [&_a]:border-border [&_a]:transition hover:[&_a]:border-muted-foreground [&_p]:m-0"
                        dangerouslySetInnerHTML={{ __html: sanitizeRichText(item) }}
                      />
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

function Section({ id, title, entries }: { id: string; title: string; entries: Entry[] }) {
  const groups = entries.reduce<{ place: string; entries: Entry[] }[]>((result, entry) => {
    const existingGroup = result.find((group) => group.place === entry.place);
    if (existingGroup) {
      existingGroup.entries.push(entry);
    } else {
      result.push({ place: entry.place, entries: [entry] });
    }
    return result;
  }, []);

  return (
    <div
      id={id}
      className="grid scroll-mt-24 gap-8 border-t border-border py-12 lg:grid-cols-[240px_1fr]"
    >
      <h2 className="font-serif text-3xl leading-tight">{title}</h2>
      <ol className="space-y-14">
        {groups.map((group) => (
          <li key={group.place}>
            <h3 className="font-serif text-2xl font-semibold leading-tight text-foreground">
              {group.place}
            </h3>

            <ol className="relative mt-7 ml-1 space-y-10 border-l border-ocean/35 pl-7 sm:ml-3 sm:pl-10">
              {group.entries.map((e) => (
                <li key={`${e.role}-${e.period}`} className="relative">
                  <span
                    aria-hidden
                    className="absolute top-2 -left-[2.05rem] h-2 w-2 rounded-full bg-ocean ring-4 ring-background sm:-left-[2.8rem]"
                  />

                  <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6">
                    <h4 className="font-serif text-xl leading-snug">
                      {e.href ? (
                        <a
                          href={e.href}
                          className="font-[inherit] underline decoration-muted-foreground underline-offset-4 transition hover:text-ocean"
                        >
                          {e.role}
                        </a>
                      ) : (
                        e.role
                      )}
                    </h4>
                    <p className="shrink-0 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      {e.period}
                    </p>
                  </div>

                  {(e.type || e.city || e.duration) && (
                    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-muted-foreground">
                      {e.type && (
                        <span className="rounded-full bg-ocean-50 px-3 py-1 uppercase tracking-[0.18em] text-ocean">
                          {e.type}
                        </span>
                      )}
                      {e.city && <span>{e.city}</span>}
                      {e.duration && <span>{e.duration}</span>}
                    </div>
                  )}

                  {e.bullets.length > 0 && (
                    <ul className="mt-4 space-y-2 text-sm leading-relaxed text-foreground/85">
                      {e.bullets.map((b, i) => (
                        <li key={i}>
                          <span
                            className="[&_a]:border-b [&_a]:border-border [&_a]:transition hover:[&_a]:border-muted-foreground [&_p]:m-0"
                            dangerouslySetInnerHTML={{ __html: sanitizeRichText(b) }}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Parcours() {
  const { data } = useSuspenseQuery(parcoursQueryOptions);
  const entriesBy = (category: string) =>
    data.entries.filter((e) => e.category === category).map(toEntry);
  const factsBy = (category: string) =>
    data.facts.filter((f) => f.category === category).map((f) => ({ k: f.label, v: f.value }));

  const sections = data.categories.map((c) => ({ id: c.slug, label: c.label }));

  return (
    <section className="mx-auto max-w-5xl px-6 pt-16 pb-24">
      <div className="relative mb-8 overflow-hidden rounded-3xl border border-border bg-background">
        <div className="absolute inset-x-0 top-0 h-px bg-ocean" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 h-px bg-ocean" aria-hidden />
        <header className="max-w-3xl px-8 pt-16 pb-10 sm:px-12 sm:pt-20 sm:pb-12">
          <p className="eyebrow text-ocean mb-12">Curriculum vitæ</p>
          <h1 className="mt-12 font-display text-5xl leading-[1.05] sm:text-6xl">
            <span className="text-foreground">Mon </span>
            <em className="text-ocean">parcours</em>
          </h1>
          <p className="mt-16 max-w-2xl text-base leading-relaxed text-muted-foreground">
            Rédactions, formations et engagements bénévoles : les différents jalons de mon parcours
            dans le journalisme et ailleurs.
          </p>
          <a
            href={cvUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-6 inline-flex items-center gap-2 text-sm text-foreground transition-colors hover:text-ocean"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            <span className="relative">
              Télécharger le CV
              <span className="absolute inset-x-0 -bottom-px h-px origin-left bg-ocean transition-transform duration-300 group-hover:scale-x-75" />
            </span>
          </a>
        </header>
      </div>

      {/* ------- SOMMAIRE ------- */}
      <nav
        aria-label="Sommaire du parcours"
        className="flex flex-wrap items-center gap-3 border-y border-border py-4"
      >
        <span className="text-[0.65rem] uppercase tracking-[0.22em] text-muted-foreground">
          Sommaire
        </span>
        {sections.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            className="flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs uppercase tracking-[0.18em] text-foreground/70 transition hover:border-foreground hover:text-foreground"
          >
            {s.label}
          </a>
        ))}
      </nav>

      {data.categories.map((c) =>
        FACT_SLUGS.has(c.slug) ? (
          <FactBlock key={c.slug} id={c.slug} title={c.label} facts={factsBy(c.slug)} />
        ) : (
          <Section key={c.slug} id={c.slug} title={c.label} entries={entriesBy(c.slug)} />
        ),
      )}
    </section>
  );
}
